import React, { useEffect } from 'react';
import WidgetPageShell from '@components/common/WidgetPageShell';
import WidgetNav from '@components/common/WidgetNav';
import FooterBrand from '@components/common/FooterBrand';
import mockPerformanceSummary from '@data/mockPerformanceSummary';
import {
  mapAnalystForecastApiToAnalystForecast,
  mapAnalystForecastApiToEnterpriseOutlook,
  mapCorporateEventApiToScheduleItems,
  mapFundamentalsApiToInvestmentIndicators,
  mapScoreApiToEnterpriseScore,
} from '@apis/performanceMappers';
import {
  getApiParamErrorNotificationMessage,
  shouldNotifyDataRequestError,
} from '@apis/client';
import { useSummaryScore } from '@hooks/useSummaryScore';
import { useSummaryFundamentals } from '@hooks/useSummaryFundamentals';
import { useSummaryCorporateEvents } from '@hooks/useSummaryCorporateEvents';
import { useSummaryAnalystForecast } from '@hooks/useSummaryAnalystForecast';
import { useSummaryStockReportPdfLink } from '@hooks/useSummaryStockReportPdfLink';
import { notify } from '@utils/notify';
import type { PerformanceSummaryData } from '@app-types/performance';
import EnterpriseScoreSection from './components/EnterpriseScoreSection';
import InvestmentIndicatorsSection from './components/InvestmentIndicatorsSection';
import AnalystForecastSection from './components/AnalystForecastSection';
import ProgressStatusSection from './components/ProgressStatusSection';
import './PerformanceSummaryPage.less';

const notifySummaryQueryError = (
  error: unknown,
  fallbackMessage: string,
): void => {
  if (!shouldNotifyDataRequestError(error)) return;

  notify({
    level: 'error',
    message: getApiParamErrorNotificationMessage(error) ?? fallbackMessage,
  });
};

/**
 * Performance Summary screen (T06).
 * Desktop container 3490:22967 (626px), mobile container 3154:4146 (375px).
 * Section order: nav, enterprise score, investment indicators + schedule,
 * analyst forecast, progress status, footer. API-driven data: Enterprise Score
 * numbers (GET /api/summary/score), investment indicators
 * (GET /api/summary/fundamentals), schedule (GET /api/summary/corporate-event),
 * and analyst forecast (GET /api/summary/stocks/analyst-forecast). The Enterprise
 * Score outlook heading/body also come from the analyst-forecast response even
 * though they render in the Enterprise Score section. Remaining sections render
 * from mock data. Layout/responsiveness stay CSS-only.
 */
const PerformanceSummaryPage: React.FC = () => {
  const scoreQuery = useSummaryScore();
  const fundamentalsQuery = useSummaryFundamentals();
  const corporateEventsQuery = useSummaryCorporateEvents();
  const analystForecastQuery = useSummaryAnalystForecast();
  const stockReportPdfLinkMutation = useSummaryStockReportPdfLink();

  useEffect(() => {
    if (scoreQuery.isError) {
      notifySummaryQueryError(scoreQuery.error, '企業スコアの取得に失敗しました。');
    }
  }, [scoreQuery.error, scoreQuery.isError]);

  useEffect(() => {
    if (fundamentalsQuery.isError) {
      notifySummaryQueryError(fundamentalsQuery.error, '投資指標の取得に失敗しました。');
    }
  }, [fundamentalsQuery.error, fundamentalsQuery.isError]);

  useEffect(() => {
    if (corporateEventsQuery.isError) {
      notifySummaryQueryError(corporateEventsQuery.error, 'スケジュールの取得に失敗しました。');
    }
  }, [corporateEventsQuery.error, corporateEventsQuery.isError]);

  useEffect(() => {
    if (analystForecastQuery.isError) {
      notifySummaryQueryError(analystForecastQuery.error, 'アナリスト予想の取得に失敗しました。');
    }
  }, [analystForecastQuery.error, analystForecastQuery.isError]);

  // Preserve static Figma labels/layout from the mock view model, but never
  // show mock dynamic values while API-backed Summary fields are loading,
  // missing, or errored.
  const enterpriseScoreFromScore = mapScoreApiToEnterpriseScore(
    scoreQuery.data,
    mockPerformanceSummary.enterpriseScore,
  );

  // Enterprise Score outlook copy ships on the analyst-forecast response, so
  // reuse that single query result here instead of adding another request.
  const enterpriseScore = mapAnalystForecastApiToEnterpriseOutlook(
    analystForecastQuery.data,
    enterpriseScoreFromScore,
  );

  const indicators = mapFundamentalsApiToInvestmentIndicators(
    fundamentalsQuery.data,
    mockPerformanceSummary.indicators,
  );

  const schedule = mapCorporateEventApiToScheduleItems(
    corporateEventsQuery.data,
    mockPerformanceSummary.schedule,
  );

  const analystForecast = mapAnalystForecastApiToAnalystForecast(
    analystForecastQuery.data,
    mockPerformanceSummary.analystForecast,
  );

  const data: PerformanceSummaryData = {
    ...mockPerformanceSummary,
    enterpriseScore,
    indicators,
    schedule,
    analystForecast: {
      ...analystForecast,
      // An errored refetch may retain cached data. Hide only the description
      // for the failed analyst query, without clearing other mapped sections.
      targetDescription: analystForecastQuery.isError ? '' : analystForecast.targetDescription,
    },
  };

  const handleOpenStockReport = async (): Promise<string> => {
    try {
      return await stockReportPdfLinkMutation.mutateAsync();
    } catch (caught) {
      if (shouldNotifyDataRequestError(caught)) {
        notify({
          level: 'error',
          message: getApiParamErrorNotificationMessage(caught) ??
            '株式レポートの取得に失敗しました。',
        });
      }
      throw caught;
    }
  };

  return (
    <WidgetPageShell className="performance-summary">
      <main className="performance-summary__body" data-node-id="3490:22967">
        <WidgetNav />
        <EnterpriseScoreSection
          data={data.enterpriseScore}
          isOpeningStockReport={stockReportPdfLinkMutation.isPending}
          onOpenStockReport={handleOpenStockReport}
        />
        <InvestmentIndicatorsSection
          indicators={data.indicators}
          schedule={data.schedule}
        />
        <AnalystForecastSection data={data.analystForecast} />
        <ProgressStatusSection data={data.progressStatus} />
      </main>
      <FooterBrand className="performance-summary__footer" />
    </WidgetPageShell>
  );
};

export default PerformanceSummaryPage;
